import React from "react";
import { useSearchParams } from "react-router-dom";
import { Building2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import ContactWarningCard from "@/components/pages/my-settings/ContactWarningCard";
import DisplayNameCard from "@/components/pages/my-settings/DisplayNameCard";
import VoicemailGreetingCard from "@/components/pages/my-settings/VoicemailGreetingCard";
import NotificationPreferencesCard from "@/components/pages/my-settings/NotificationPreferencesCard";
import { useGetHomeQuery } from "@/store/rest_api";

export default function MySettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "preferences";

  const { data: user, isLoading } = useGetHomeQuery();

  const onSelectTab = (tab: string) => {
    setSearchParams(tab === "preferences" ? {} : { tab });
  };

  if (isLoading) {
    return (
      <div className="p-4 text-foreground">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-64 w-full max-w-2xl" />
      </div>
    );
  }

  return (
    <div className="p-4 text-foreground">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">My settings</h1>
        <p className="text-muted-foreground text-sm">
          How the app behaves for you inside this organisation. Your name, password and sign in methods live
          in Account settings.
        </p>
        {user?.activeOrganisation?.name && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border bg-muted/40 px-3 py-1 text-xs font-semibold">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
            {user.activeOrganisation.name}
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={onSelectTab}>
        <TabsList>
          <TabsTrigger value="preferences">Preferences</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="preferences" className="mt-6 max-w-2xl space-y-6">
          <DisplayNameCard />
          <ContactWarningCard />
          <VoicemailGreetingCard />
        </TabsContent>

        <TabsContent value="notifications" className="mt-6 max-w-2xl">
          <NotificationPreferencesCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
